
import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} logoTrigger
 * @property {HTMLElement} dialogClose
 * @property {HTMLInputElement} [passwordInput]
 */

class PasswordPageComponent extends Component {
  /** @type {string[]} */
  requiredRefs = ['dialog', 'logoTrigger', 'dialogClose'];

  /** @type {HTMLElement | null} */
  #lastFocusTarget = null;
  /** @type {number} */
  #logoTapCount = 0;
  /** @type {number | null} */
  #logoTapTimeoutId = null;
  /** @type {boolean} */
  #didAutoOpenOnError = false;
  /** @type {number} */
  #ignoreBackdropClickUntil = 0;
  /** @type {HTMLElement | null} */
  #boundLogoTrigger = null;
  /** @type {HTMLDialogElement | null} */
  #boundDialog = null;
  /** @type {HTMLElement | null} */
  #boundDialogClose = null;

  #documentKeydownHandler = (/** @type {KeyboardEvent} */ event) => {
    this.#handleDocumentKeydown(event);
  };

  #dialogClickHandler = (/** @type {MouseEvent} */ event) => {
    this.#handleDialogClick(event);
  };

  #dialogCloseEventHandler = () => {
    this.#handleDialogClosed();
  };

  #dialogCloseButtonHandler = (/** @type {Event} */ event) => {
    event.preventDefault();

    const dialog = this.refs.dialog;
    if (dialog instanceof HTMLDialogElement && dialog.open) {
      dialog.close('dismiss');
    }
  };

  #logoPointerUpHandler = (/** @type {PointerEvent} */ event) => {
    this.#handleLogoPointerUp(event);
  };

  #logoClickFallbackHandler = (/** @type {MouseEvent} */ event) => {
    this.#handleLogoClickFallback(event);
  };

  connectedCallback() {
    super.connectedCallback();

    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '-1');
    }

    document.addEventListener('keydown', this.#documentKeydownHandler);
    this.#bindDomListeners();
    this.#autoOpenDialogOnPasswordError();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#unbindDomListeners();
    this.#bindDomListeners();
    this.#autoOpenDialogOnPasswordError();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('keydown', this.#documentKeydownHandler);
    this.#unbindDomListeners();
    this.#resetLogoTapCounter();
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleDocumentKeydown(event) {
    if (event.repeat) return;
    if (event.shiftKey) return;

    const key = String(event.key || '').toLowerCase();
    if (key !== 'p') return;

    const isApplePlatform =
      typeof navigator !== 'undefined' &&
      /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '');

    const matchesMacShortcut = isApplePlatform && event.metaKey && event.ctrlKey && !event.altKey;
    const matchesOtherShortcut = !isApplePlatform && event.ctrlKey && event.altKey && !event.metaKey;

    if (!matchesMacShortcut && !matchesOtherShortcut) return;

    if (this.#isEditableTarget(event.target)) return;

    event.preventDefault();
    this.#openDialog(document.activeElement instanceof HTMLElement ? document.activeElement : this);
  }

  /**
   * @param {EventTarget | null} target
   */
  #isEditableTarget(target) {
    if (!(target instanceof Element)) return false;

    return Boolean(
      target.closest(
        'input, textarea, select, [contenteditable="true"], [contenteditable=""], [contenteditable]:not([contenteditable="false"])'
      )
    );
  }

  #bindDomListeners() {
    const logoTrigger = this.refs.logoTrigger;
    const dialog = this.refs.dialog;
    const dialogClose = this.refs.dialogClose;

    if (logoTrigger instanceof HTMLElement) {
      logoTrigger.addEventListener('pointerup', this.#logoPointerUpHandler);
      logoTrigger.addEventListener('click', this.#logoClickFallbackHandler);
      this.#boundLogoTrigger = logoTrigger;
    }

    if (dialog instanceof HTMLDialogElement) {
      dialog.addEventListener('click', this.#dialogClickHandler);
      dialog.addEventListener('close', this.#dialogCloseEventHandler);
      this.#boundDialog = dialog;
    }

    if (dialogClose instanceof HTMLElement) {
      dialogClose.addEventListener('click', this.#dialogCloseButtonHandler);
      this.#boundDialogClose = dialogClose;
    }
  }

  #unbindDomListeners() {
    if (this.#boundLogoTrigger) {
      this.#boundLogoTrigger.removeEventListener('pointerup', this.#logoPointerUpHandler);
      this.#boundLogoTrigger.removeEventListener('click', this.#logoClickFallbackHandler);
      this.#boundLogoTrigger = null;
    }

    if (this.#boundDialog) {
      this.#boundDialog.removeEventListener('click', this.#dialogClickHandler);
      this.#boundDialog.removeEventListener('close', this.#dialogCloseEventHandler);
      this.#boundDialog = null;
    }

    if (this.#boundDialogClose) {
      this.#boundDialogClose.removeEventListener('click', this.#dialogCloseButtonHandler);
      this.#boundDialogClose = null;
    }
  }

  #autoOpenDialogOnPasswordError() {
    if (this.#didAutoOpenOnError) return;

    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (dialog.open) return;

    const hasPasswordError = dialog.querySelector('[data-password-error]');
    if (!hasPasswordError) return;

    this.#didAutoOpenOnError = true;
    this.#openDialog(this);
  }

  /**
   * @param {PointerEvent} event
   */
  #handleLogoPointerUp(event) {
    if (event.pointerType === 'mouse') return;
    this.#registerLogoTap();
  }

  /**
   * Safari fallback if Pointer Events are not available/reliable on touch.
   * @param {MouseEvent} event
   */
  #handleLogoClickFallback(event) {
    if (typeof PointerEvent === 'function') return;

    const isTouchPrimaryInput =
      typeof window.matchMedia === 'function' && window.matchMedia('(hover: none), (pointer: coarse)').matches;

    if (!isTouchPrimaryInput) return;

    this.#registerLogoTap();
  }

  #registerLogoTap() {
    this.#logoTapCount += 1;

    if (this.#logoTapCount >= 5) {
      this.#resetLogoTapCounter();
      this.#openDialog(this.refs.logoTrigger instanceof HTMLElement ? this.refs.logoTrigger : this);
      return;
    }

    if (this.#logoTapTimeoutId != null) {
      window.clearTimeout(this.#logoTapTimeoutId);
    }

    this.#logoTapTimeoutId = window.setTimeout(() => {
      this.#resetLogoTapCounter();
    }, 1500);
  }

  #resetLogoTapCounter() {
    this.#logoTapCount = 0;

    if (this.#logoTapTimeoutId != null) {
      window.clearTimeout(this.#logoTapTimeoutId);
      this.#logoTapTimeoutId = null;
    }
  }

  /**
   * @param {Event} event
   */
  #handleDialogClick(event) {
    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLDialogElement)) return;

    if (event.target === dialog) {
      if (typeof performance !== 'undefined' && performance.now() < this.#ignoreBackdropClickUntil) return;
      dialog.close('dismiss');
    }
  }

  #handleDialogClosed() {
    const nextFocusTarget = this.#lastFocusTarget;
    this.#lastFocusTarget = null;

    if (nextFocusTarget instanceof HTMLElement && nextFocusTarget.isConnected) {
      nextFocusTarget.focus({ preventScroll: true });
    }
  }

  /**
   * @param {HTMLElement} trigger
   */
  #openDialog(trigger) {
    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLDialogElement)) return;

    this.#lastFocusTarget = trigger;

    if (!dialog.open) {
      try {
        dialog.showModal();
        if (typeof performance !== 'undefined') {
          this.#ignoreBackdropClickUntil = performance.now() + 250;
        }
      } catch {
        return;
      }
    }

    const passwordInput = this.refs.passwordInput;
    if (passwordInput instanceof HTMLElement) {
      requestAnimationFrame(() => {
        passwordInput.focus({ preventScroll: true });
        if (passwordInput instanceof HTMLInputElement) {
          passwordInput.select();
        }
      });
    }
  }
}

if (!customElements.get('password-page-component')) {
  customElements.define('password-page-component', PasswordPageComponent);
}
